/* eslint-disable react/require-default-props */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Close from '@mui/icons-material/Close';

import { createIconComponent } from 'uikit/icons';
import { Button } from 'uikit/buttons';

import styles from '../styles/uikitInputsInput.module.scss';

const TextInput = forwardRef(
  (
    {
      id,
      className = '',
      placeholder = '',
      value = '',
      onChange = null,
      onKeyUp = () => {},
      onKeyDown = null,
      onFocus = () => {},
      onBlur = () => {},
      onClick = null,
      icon = null,
      isDisabled = false,
      isReadOnly = false,
      isRequired = false,
      isError = false,
      shouldShowCloseButton = false,
      testId = '',
      inputTestId = '',
      buttonTestId = '',
      autoComplete = 'off',
      autoCorrect = 'off',
      spellCheck = 'false',
      name = '',
      ariaLabel = '',
      maxLength = null,
      minLength = 0,
      buttonAriaLabel = '',
      size = 'medium',
      shouldHideBorder = false,
      type = 'text',
    },
    ref,
  ) => {
    const CLASSES = {
      size: {
        small: styles.isSizeSmall,
        medium: styles.isSizeMedium, // default - no matching css class
        large: styles.isSizeLarge,
      },
    };

    const onCloseButtonClick = () => {
      if (onClick) onClick();
    };

    return (
      <div
        className={classnames(
          styles.element,
          {
            [styles.isIconVisible]: !!icon,
            [CLASSES.size[size]]: size,
            [styles.shouldHideBorder]: shouldHideBorder,
            [styles.isError]: isError,
          },
          className,
        )}
        data-testid={testId}
      >
        {icon &&
          createIconComponent({
            icon,
            className: classnames(styles.icon, icon.props.className),
          })}
        <input
          id={id}
          className={className}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={ref}
          value={value}
          disabled={isDisabled}
          readOnly={isReadOnly}
          required={isRequired && !isReadOnly && !isDisabled}
          data-testid={inputTestId}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          spellCheck={spellCheck}
          name={name}
          aria-label={ariaLabel}
          maxLength={maxLength}
          minLength={minLength}
          type={type}
        />
        {shouldShowCloseButton && (
          <Button
            isDisabled={isDisabled}
            variant="ghost"
            icon={<Close />}
            size="xsmall"
            onClick={onCloseButtonClick}
            className={styles.button}
            data-testid={buttonTestId}
            ariaLabel={buttonAriaLabel}
          />
        )}
      </div>
    );
  },
);

TextInput.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onKeyUp: PropTypes.func,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isRequired: PropTypes.bool,
  isError: PropTypes.bool,
  shouldShowCloseButton: PropTypes.bool,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  testId: PropTypes.string,
  inputTestId: PropTypes.string,
  buttonTestId: PropTypes.string,
  autoComplete: PropTypes.oneOfType([PropTypes.oneOf(['on', 'off']), PropTypes.string]),
  autoCorrect: PropTypes.oneOf(['on', 'off']),
  spellCheck: PropTypes.oneOf(['true', 'false']),
  name: PropTypes.string,
  ariaLabel: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  buttonAriaLabel: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  shouldHideBorder: PropTypes.bool,
  type: PropTypes.oneOf(['email', 'password', 'search', 'tel', 'text', 'time']),
};

export { TextInput as TextInputStorybook };
export default React.memo(TextInput);
